import React, { forwardRef, useMemo } from "react";
import ProductSheet from "./ProductSheet";
import ProductTiles from "./ProductTiles";
import ProductIntro from "./ProductIntro";
import ProductExport from "./ProductExport";
import { urlDomain } from "../utils/productMode";

/**
 * Product mode: the mobile view of a table's rows, built to be screen-recorded.
 *
 * A strip at the top frames what is on screen — the source site, how many
 * products were gathered and how many fields each has — above either the
 * sheet layout (rows under a column header) or the grid of image tiles. Two
 * optional overlays play on top: the intro when the view opens, and the
 * export card when an export starts.
 */

const DatasetStrip = ({ source, totalRows, fieldCount, hiddenCount, isDarkMode, onOpenSettings }) => {
  const domain = urlDomain(source?.url);
  const title = source?.name || domain || "Products";
  const meta = [
    source?.name && domain ? domain : null,
    `${totalRows.toLocaleString()} ${totalRows === 1 ? "product" : "products"}`,
    `${fieldCount} ${fieldCount === 1 ? "field" : "fields"}`,
    hiddenCount > 0 ? `${hiddenCount.toLocaleString()} without images hidden` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="flex items-center gap-3 px-3 pt-3 pb-2">
      {source?.favicon && (
        <img
          src={source.favicon}
          alt=""
          className="w-5 h-5 rounded shrink-0"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      )}
      <div className="min-w-0 flex-1">
        <div className={`text-[13px] font-semibold tracking-tight truncate ${isDarkMode ? "text-gray-100" : "text-gray-900"}`}>{title}</div>
        <div className="text-[11px] tabular-nums leading-snug text-gray-500">{meta}</div>
      </div>
      <button
        type="button"
        onClick={onOpenSettings}
        className={`shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors duration-150 ${
          isDarkMode
            ? "bg-white/[0.04] border-white/[0.06] text-gray-300 hover:text-white hover:bg-white/[0.08]"
            : "bg-white border-gray-200 text-gray-600 hover:text-gray-900 hover:bg-gray-50"
        }`}
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
        </svg>
        Customize
      </button>
    </div>
  );
};

const ProductView = forwardRef(
  (
    {
      rows,
      settings,
      fields,
      priceColumnId,
      recordColumns,
      resolveTile,
      isDarkMode,
      isPaginating = false,
      selectedRowId,
      onOpenGallery,
      onOpenSettings,
      source,
      totalRows,
      revealKey,
      intro = null,
      exportAnimation = null,
    },
    ref
  ) => {
    const { items, hiddenCount } = useMemo(() => {
      const list = [];
      let hidden = 0;
      for (const row of rows || []) {
        const image = resolveTile(row);
        if (!image.url && settings.hideWithoutImages) {
          hidden += 1;
          continue;
        }
        list.push({ row, image });
      }
      return { items: list, hiddenCount: hidden };
    }, [rows, resolveTile, settings.hideWithoutImages]);

    const isGrid = settings.layout === "grid";

    // The intro lands one image per row, in row order, whatever is hidden below.
    const overlayImages = useMemo(
      () => (rows || []).map((row) => resolveTile(row).url).filter(Boolean),
      [rows, resolveTile]
    );

    return (
      <div className={`relative flex flex-col flex-1 min-h-0 h-full ${isDarkMode ? "bg-[#0a0a0b]" : "bg-[#fafafa]"}`}>
        {intro && (rows || []).length > 0 && (
          <ProductIntro
            key={intro.runKey}
            images={overlayImages}
            total={totalRows ?? (rows || []).length}
            source={source}
            durationMs={intro.durationMs}
            onLeave={intro.onLeave}
            onDone={intro.onDone}
          />
        )}
        {exportAnimation && (
          <ProductExport
            key={exportAnimation.runKey}
            total={totalRows ?? (rows || []).length}
            format={exportAnimation.format}
            durationMs={exportAnimation.durationMs}
            onDone={exportAnimation.onDone}
          />
        )}

        {/* Loading overlay for pagination */}
        {isPaginating && (
          <div className={`absolute inset-0 z-40 flex items-center justify-center backdrop-blur-[2px] ${isDarkMode ? "bg-black/50" : "bg-white/50"}`}>
            <div className={`w-5 h-5 border-2 border-t-transparent rounded-full animate-spin ${isDarkMode ? "border-white/40" : "border-gray-400"}`} />
          </div>
        )}

        <div ref={ref} className="flex-1 min-h-0 overflow-y-auto custom-scrollbar">
          <DatasetStrip
            source={source}
            totalRows={totalRows ?? (rows || []).length}
            fieldCount={recordColumns.length}
            hiddenCount={hiddenCount}
            isDarkMode={isDarkMode}
            onOpenSettings={onOpenSettings}
          />

          {items.length === 0 ? (
            <div className="px-6 py-16 text-center">
              <p className={`text-sm font-medium ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>No products to show on this page</p>
              <p className="mt-1 text-xs text-gray-500">
                {hiddenCount > 0
                  ? "Every product here is missing an image. Turn off “Hide products without images” in Customize to show them anyway."
                  : "Nothing to show."}
              </p>
            </div>
          ) : isGrid ? (
            // Keyed so a new page (or a fresh open) replays the staggered reveal.
            <ProductTiles
              key={revealKey}
              items={items}
              fields={fields}
              settings={settings}
              isDarkMode={isDarkMode}
              selectedRowId={selectedRowId}
              onOpenGallery={onOpenGallery}
            />
          ) : (
            <ProductSheet
              key={revealKey}
              items={items}
              fields={fields}
              priceColumnId={priceColumnId}
              recordColumns={recordColumns}
              settings={settings}
              isDarkMode={isDarkMode}
              selectedRowId={selectedRowId}
              onOpenGallery={onOpenGallery}
            />
          )}
        </div>
      </div>
    );
  }
);
ProductView.displayName = "ProductView";

export default ProductView;
